import { ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { AddressAutocomplete } from '../../components/address-autocomplete';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';
import { Field, Label } from '../../components/fieldset';
import { Heading } from '../../components/heading';
import { Input } from '../../components/input';
import { Textarea } from '../../components/textarea';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { DynamicFieldFactory } from '../../utils/DynamicFieldFactory';
import { reactSelectReset } from '../../utils/utils';

const facilityValidationSchema = z.object({
  phoneNumber: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a phone number',
        fatal: false,
      });
      return;
    }
    const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    if (!phoneRegex.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid phone number',
        fatal: true,
      });
    }
  }),
  zipCode: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a ZIP code',
        fatal: false,
      });
      return;
    }
    if (!val.match(/^\d{5}$/)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ZIP code must be exactly 5 digits',
        fatal: true,
      });
    }
  }),
});

const ValidationMessage = ({
  message,
  icon,
  colorClass,
}: {
  message?: string;
  icon?: React.ReactNode;
  colorClass?: string;
}) => {
  if (!message) return null;
  return (
    <div className={`tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-sm ${colorClass}`}>
      {icon}
      {message}
    </div>
  );
};

interface EditFacilityDialogProps {
  open: boolean;
  disableNameEdit?: boolean;
  onClose: () => void;
  facilityId: string;
  initialData: any;
  onSuccess?: () => void;
  facilityTypesWithDynamicFields: Record<string, any[]>;
}

const createValidationMessages = (errors: z.ZodError) => {
  const newValidationMessages: Record<
    string,
    { message: string; icon: React.ReactNode; colorClass: string }
  > = {};

  errors.errors.forEach((err) => {
    const field = err.path[0];
    newValidationMessages[field] = {
      message: err.message,
      icon: err.fatal ? (
        <XCircleIcon className="tw:h-5 tw:w-5" />
      ) : (
        <ExclamationTriangleIcon className="tw:h-5 tw:w-5" />
      ),
      colorClass: err.fatal
        ? 'tw:text-stoplight-danger tw:dark:text-red-500'
        : 'tw:text-stoplight-warning tw:dark:text-stoplight-warning',
    };
  });

  return newValidationMessages;
};

export const EditFacilityDialog: React.FC<EditFacilityDialogProps> = ({
  open,
  disableNameEdit,
  onClose,
  facilityId,
  initialData,
  onSuccess,
  facilityTypesWithDynamicFields,
}) => {
  const [name, setName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [streetAddress2, setStreetAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState<string | undefined>(undefined);
  const [zipCode, setZipCode] = useState('');
  const [type, setType] = useState('');
  const [ownership, setOwnership] = useState('');
  const [emr, setEmr] = useState('');
  const [overview, setOverview] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dynamicFields, setDynamicFields] = useState<Record<string, any>>({});
  const [originalType, setOriginalType] = useState<string>('');
  const [originalFacilityTypeFields, setOriginalFacilityTypeFields] = useState<any[]>([]);
  const [showTypeChangeWarning, setShowTypeChangeWarning] = useState(false);
  const [validationMessages, setValidationMessages] = useState<
    Record<string, { message: string; icon: React.ReactNode; colorClass: string }>
  >({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const dynamicChoicesCategories = useMemo(() => {
    const fields = initialData?.facility_type_fields || [];
    const selectFields = fields.filter(
      (field: any) => field.field_type === 'select' && field.choices_category
    );
    return selectFields.map((field: any) => field.choices_category);
  }, [initialData]);

  const allCategories = useMemo(() => {
    const baseCategories = [
      'FacilityTypes',
      'HospitalOwnershipChoices',
      'EMRChoices',
      'PostalStateChoices',
      'FacilityContactRoles',
      'Specialty',
    ];
    return [...new Set([...baseCategories, ...dynamicChoicesCategories])];
  }, [dynamicChoicesCategories]);

  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories(allCategories);

  const facilityTypesOptions = useMemo(
    () => codeDefinitionsByCategory?.FacilityTypes || [],
    [codeDefinitionsByCategory]
  );

  const hospitalOwnershipOptions = useMemo(
    () => codeDefinitionsByCategory?.HospitalOwnershipChoices || [],
    [codeDefinitionsByCategory]
  );

  const emrOptions = useMemo(
    () => codeDefinitionsByCategory?.EMRChoices || [],
    [codeDefinitionsByCategory]
  );

  const stateOptions = useMemo(
    () => codeDefinitionsByCategory?.PostalStateChoices || [],
    [codeDefinitionsByCategory]
  );

  const dynamicFieldChoices = useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const choicesMap: Record<string, any[]> = {};
    dynamicChoicesCategories.forEach((category: string) => {
      choicesMap[category] = codeDefinitionsByCategory[category] || [];
    });
    return choicesMap;
  }, [codeDefinitionsByCategory, dynamicChoicesCategories]);

  const codeDefinitionsMap = useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const map: Record<string, string> = {};
    Object.values(codeDefinitionsByCategory)
      .flat()
      .forEach((def) => {
        map[def.code] = def.title;
      });
    return map;
  }, [codeDefinitionsByCategory]);

  // Reset form state when dialog opens to ensure it shows current facility data
  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (open && initialData) {
      setName(initialData.name || '');
      setStreetAddress(initialData.street_address || '');
      setStreetAddress2(initialData.street_address2 || '');
      setCity(initialData.city || '');
      setState(initialData.state);
      setZipCode(initialData.zip_code || '');
      setType(initialData.facility_type || '');
      setOwnership(initialData.hospital_ownership || '');
      setEmr(initialData.emr || '');
      setOverview(initialData.extra_info || '');
      setPhoneNumber(initialData.phone_number || '');

      if (initialData.facility_type_fields) {
        const initialDynamicFields: Record<string, any> = {};
        initialData.facility_type_fields.forEach((field: any) => {
          initialDynamicFields[field.field_name] = field.field_value ?? '';
        });
        setDynamicFields(initialDynamicFields);
      } else {
        setDynamicFields({});
      }

      setShowTypeChangeWarning(false);
      setValidationMessages({});
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) {
      setOriginalType('');
      setOriginalFacilityTypeFields([]);
    }
  }, [open]);

  useEffect(() => {
    if (hasAttemptedSubmit) {
      const result = facilityValidationSchema.safeParse({
        phoneNumber,
        zipCode,
      });

      if (!result.success) {
        setValidationMessages(createValidationMessages(result.error));
      } else {
        setValidationMessages({});
      }
    }
  }, [phoneNumber, zipCode, hasAttemptedSubmit]);

  useEffect(() => {
    if (!open) {
      setValidationMessages({});
      setHasAttemptedSubmit(false);
    }
  }, [open]);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: any) =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesUpdate({
        id: facilityId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Facility updated successfully!');
      queryClient.invalidateQueries();
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to update facility' });
    },
  });

  const handleTypeChange = (newType: string) => {
    if (newType !== type) {
      // Only store original type and fields if we haven't already
      if (!originalType) {
        setOriginalType(type);
        setOriginalFacilityTypeFields(initialData.facility_type_fields || []);
      }

      setShowTypeChangeWarning(true);

      setType(newType);
      initialData.facility_type = newType;

      if (facilityTypesWithDynamicFields[newType]) {
        initialData.facility_type_fields = facilityTypesWithDynamicFields[newType];
        const initialDynamicFields: Record<string, any> = {};
        initialData.facility_type_fields.forEach((field: any) => {
          initialDynamicFields[field.field_name] = '';
        });
        setDynamicFields(initialDynamicFields);
      } else {
        initialData.facility_type_fields = [];
        setDynamicFields({});
      }
    }
  };

  const handleUndoTypeChange = () => {
    setType(originalType);
    initialData.facility_type = originalType;

    initialData.facility_type_fields = originalFacilityTypeFields;

    const originalDynamicFields: Record<string, any> = {};
    originalFacilityTypeFields.forEach((field: any) => {
      originalDynamicFields[field.field_name] = field.field_value ?? '';
    });
    setDynamicFields(originalDynamicFields);
    setShowTypeChangeWarning(false);
  };

  const handleDynamicFieldChange = (fieldName: string, value: any) => {
    setDynamicFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    const result = facilityValidationSchema.safeParse({
      phoneNumber,
      zipCode,
    });

    if (!result.success) {
      const hasFatalErrors = result.error.errors.some((err) => err.fatal);
      if (hasFatalErrors) {
        setValidationMessages(createValidationMessages(result.error));
        toast.error('Please fix the validation errors before submitting.');
        return;
      }
    }

    mutation.mutate({
      name,
      street_address: streetAddress,
      street_address2: streetAddress2,
      city,
      state,
      zip_code: zipCode,
      facility_type: type,
      hospital_ownership: ownership,
      emr,
      extra_info: overview,
      phone_number: phoneNumber,
      facility_type_fields: initialData.facility_type_fields
        ? initialData.facility_type_fields.map((field: any) => ({
            // TODO: JOE URGENT
            // the specificity of this payload will cause serious problems and is not scalable
            // the complexity of the FTF stems from handling the type change and undoing the type change
            // we need to keep initialData separate from the "currentData", which will be swapped out
            // based on the type change
            field_name: field.field_name,
            field_title: field.field_title,
            field_type: field.field_type,
            field_order: field.field_order,
            field_required: field.field_required,
            field_unit: field.field_unit,
            choices_category: field.choices_category,
            show_on_connection: field.show_on_connection,
            show_on_communication: field.show_on_communication,
            field_value:
              field.field_type === 'number'
                ? dynamicFields[field.field_name] === ''
                  ? null
                  : Number(dynamicFields[field.field_name])
                : dynamicFields[field.field_name],
          }))
        : [],
    });
  };

  const handleClose = () => {
    // If we have original values stored, restore them before closing
    if (originalType) {
      initialData.facility_type = originalType;
      initialData.facility_type_fields = originalFacilityTypeFields;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} className="tw:max-w-4xl">
      <DialogTitle className="tw:dark:!text-white">Edit Facility</DialogTitle>
      {showTypeChangeWarning && (
        <div className="tw:mt-2 tw:mb-4 tw:rounded-md tw:bg-stoplight-warning/10 tw:p-4">
          <div className="tw:flex">
            <div className="tw:flex-shrink-0">
              <i className="fa-solid fa-triangle-exclamation tw:text-stoplight-warning" />
            </div>
            <div className="tw:ml-3">
              <p className="tw:text-sm tw:text-stoplight-warning">
                Changing from {codeDefinitionsMap[originalType] || originalType} removes information
                specific to that facility type. If you did not mean to change the type,{' '}
                <button
                  type="button"
                  onClick={handleUndoTypeChange}
                  className="tw:font-medium tw:underline tw:hover:text-stoplight-warning/80"
                >
                  click here to undo.
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="tw:space-y-6">
            {/* Facility Info */}
            <div className="tw:space-y-6" data-1p-ignore>
              <Field className="tw:col-span-2">
                <Label htmlFor="facility-name">
                  Facility Name <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  id="facility-name"
                  value={name}
                  disabled={disableNameEdit}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Field>
              <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6">
                <Field>
                  <Label htmlFor="facility-type">
                    Type <span className="tw:text-stoplight-danger">*</span>
                  </Label>
                  <Select
                    options={facilityTypesOptions.map((option) => ({
                      value: option.code,
                      label: option.title,
                    }))}
                    value={
                      facilityTypesOptions.find((t) => t.code === type)
                        ? {
                            value: type,
                            label: facilityTypesOptions.find((t) => t.code === type)?.title || '',
                          }
                        : null
                    }
                    onChange={(option) => handleTypeChange(option?.value || '')}
                    placeholder="Select a facility type"
                    className="tw:w-full"
                    styles={reactSelectReset}
                  />
                </Field>
                <Field>
                  <Label>Ownership</Label>
                  <Select
                    options={hospitalOwnershipOptions.map((option) => ({
                      value: option.code,
                      label: option.title,
                    }))}
                    value={
                      hospitalOwnershipOptions.find((t) => t.code === ownership)
                        ? {
                            value: ownership,
                            label:
                              hospitalOwnershipOptions.find((t) => t.code === ownership)?.title ||
                              '',
                          }
                        : null
                    }
                    onChange={(option) => setOwnership(option?.value || '')}
                    placeholder="Select an option"
                    className="tw:w-full"
                    styles={reactSelectReset}
                  />
                </Field>
              </div>
              <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6">
                <Field>
                  <Label htmlFor="facility-emr">EMR</Label>
                  <Select
                    options={emrOptions.map((option) => ({
                      value: option.code,
                      label: option.title,
                    }))}
                    value={
                      emrOptions.find((t) => t.code === emr)
                        ? {
                            value: emr,
                            label: emrOptions.find((t) => t.code === emr)?.title || '',
                          }
                        : null
                    }
                    onChange={(option) => setEmr(option?.value || '')}
                    placeholder="Select an option"
                    className="tw:w-full"
                    styles={reactSelectReset}
                  />
                </Field>
                <Field>
                  <Label htmlFor="facility-phone">Phone Number</Label>
                  <Input
                    id="facility-phone"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Phone Number"
                  />
                  <ValidationMessage
                    message={validationMessages?.phoneNumber?.message}
                    icon={validationMessages?.phoneNumber?.icon}
                    colorClass={validationMessages?.phoneNumber?.colorClass}
                  />
                </Field>
              </div>
            </div>

            {/* Address fields */}
            <div>
              <Field className="tw:md:col-span-2">
                <Label>Address</Label>
              </Field>
              <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6">
                <Field>
                  <AddressAutocomplete
                    id="facility-street-address"
                    value={streetAddress}
                    className="tw:mt-3"
                    onChange={(value) => setStreetAddress(value)}
                    onPlaceSelected={(addressComponents) => {
                      setStreetAddress(addressComponents.streetAddress);
                      setStreetAddress2(addressComponents.streetAddress2 || '');
                      setCity(addressComponents.city);
                      setState(addressComponents.state);
                      setZipCode(addressComponents.zipCode);
                    }}
                    placeholder="Street Address"
                  />
                </Field>
                <Field>
                  <Input
                    id="facility-street-address2"
                    value={streetAddress2}
                    className="tw:mt-3"
                    onChange={(e) => setStreetAddress2(e.target.value)}
                    placeholder="Street Address 2"
                  />
                </Field>
                <Field>
                  <Input
                    id="facility-city"
                    value={city}
                    className="tw:mt-3"
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                  />
                </Field>
                <Field>
                  <Select
                    options={stateOptions.map((option) => ({
                      value: option.code,
                      label: option.code,
                    }))}
                    value={
                      stateOptions.find((s) => s.code === state)
                        ? {
                            value: state || '',
                            label: state || '',
                          }
                        : null
                    }
                    onChange={(option) => setState(option?.value ?? undefined)}
                    placeholder="State"
                    className="tw:mt-3 tw:w-full"
                    styles={reactSelectReset}
                  />
                </Field>
                <Field>
                  <Input
                    id="facility-zip"
                    value={zipCode}
                    className="tw:mt-3"
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="Zip Code"
                  />
                  <ValidationMessage
                    message={validationMessages?.zipCode?.message}
                    icon={validationMessages?.zipCode?.icon}
                    colorClass={validationMessages?.zipCode?.colorClass}
                  />
                </Field>
              </div>
            </div>

            {/* Overview */}
            <Field>
              <Label htmlFor="facility-overview">Facility Overview</Label>
              <Textarea
                id="facility-overview"
                value={overview}
                onChange={(e) => setOverview(e.target.value)}
              />
            </Field>

            {/* Dynamic Fields */}
            {initialData?.facility_type_fields && initialData.facility_type_fields.length > 0 && (
              <div
                className={`tw:rounded-lg tw:p-4 tw:bg-zinc-100 tw:dark:bg-zinc-800 ${
                  showTypeChangeWarning
                    ? 'tw:bg-stoplight-warning/10 tw:dark:!bg-stoplight-warning/10'
                    : ''
                }`}
              >
                {showTypeChangeWarning && (
                  <div className="tw:mb-4">
                    <p className="tw:mt-1 tw:text-sm tw:text-stoplight-warning">
                      These fields are specific to the new facility type.
                    </p>
                  </div>
                )}
                <div>
                  <Heading className="tw:dark:!text-white/90">
                    {codeDefinitionsMap[type] || type} Fields
                  </Heading>
                </div>
                <DynamicFieldFactory
                  fields={initialData.facility_type_fields.map((field: any) => ({
                    ...field,
                    field_value: dynamicFields[field.field_name],
                    choices:
                      field.field_type === 'select'
                        ? dynamicFieldChoices[field.choices_category] || []
                        : undefined,
                  }))}
                  columns={2}
                  onChange={handleDynamicFieldChange}
                  readOnly={false}
                />
              </div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button type="button" plain onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" color="brandgreen0" disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
